import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { Square, Trash2 } from "lucide-react";
import type { JSX } from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { V3Button } from "../v3/button";

// Stop in the v3.2 recorder: write the note (the default, focused, so Stop
// then Enter is as fast as before) or discard the recording. Escape, the
// scrim and "Keep recording" carry on. Same sheet as V3ConfirmSheet: a
// bottom sheet on phones and a small centred card from 640px.
export const RecorderStopSheet = ({
  open,
  onOpenChange,
  onStop,
  onDiscard,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStop: () => void;
  /** Absent: the sheet only offers to stop. */
  onDiscard?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const stopButton = useRef<HTMLButtonElement>(null);
  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <AlertDialog.Content
          className={cn(
            "fixed z-50 flex flex-col gap-2 bg-v3-panel font-v3 text-v3-text shadow-v3-pop outline-hidden",
            "max-sm:inset-x-0 max-sm:bottom-0 max-sm:rounded-t-[22px] max-sm:px-5 max-sm:pt-3 max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))] max-sm:data-[state=open]:animate-in max-sm:data-[state=open]:slide-in-from-bottom-8",
            "sm:left-1/2 sm:top-1/2 sm:w-[380px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[18px] sm:p-6 sm:data-[state=open]:animate-in sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:fade-in-0",
          )}
          data-testid="recorder-stop-sheet"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            stopButton.current?.focus();
          }}
        >
          <div
            aria-hidden
            className="mx-auto mb-1 h-1 w-9 rounded-full bg-v3-surface-3 sm:hidden"
          />
          <AlertDialog.Title className="text-[17px] font-semibold leading-6">
            {t("recorderV32.stopSheet.title")}
          </AlertDialog.Title>
          <AlertDialog.Description className="text-[14px] leading-relaxed text-v3-text-2">
            {onDiscard
              ? t("recorderV32.stopSheet.description")
              : t("recorderV32.stopSheet.descriptionStopOnly")}
          </AlertDialog.Description>
          <div className="mt-3 flex flex-col gap-2">
            <AlertDialog.Action asChild>
              <V3Button
                ref={stopButton}
                className="w-full"
                data-testid="recorder-stop-write"
                size="lg"
                variant="primary"
                onClick={onStop}
              >
                <Square aria-hidden className="fill-current" />
                {t("recorderV32.stopSheet.stop")}
              </V3Button>
            </AlertDialog.Action>
            {onDiscard && (
              <V3Button
                className="w-full"
                data-testid="recorder-stop-discard"
                size="lg"
                variant="destructive"
                onClick={onDiscard}
              >
                <Trash2 aria-hidden />
                {t("recorderV32.discard")}
              </V3Button>
            )}
            <AlertDialog.Cancel asChild>
              <V3Button className="w-full" size="lg" variant="ghost">
                {t("recorderV32.stopSheet.keep")}
              </V3Button>
            </AlertDialog.Cancel>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};
